import cn from 'classnames';
import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { Alert } from '@mui/material';
import { BreadCrumbGroup } from 'apps/sigma2-ui/src/components/ui/BreadcrumbGroup';
import { BreadCrumbItem } from 'apps/sigma2-ui/src/components/ui/BreadcrumbGroup';
import { calcPayGapGroup } from 'libs/end-to-end/src/pay-correction/PayGapHelpers';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { CalibrationLevelBarChart } from 'apps/sigma2-ui/src/copilot/components/OptimizationLevelBarChart';
import { CalibrationLevelRadarChart } from 'apps/sigma2-ui/src/copilot/components/OptimizationLevelRadarChart';
import { checkCanAnalyze } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { checkLoadingStatus } from 'apps/sigma2-ui/src/custom-viz/lib/checkLoadingStatus';
import { constructPayCorrectionRequest } from 'apps/sigma2-ui/src/pay-correction/hooks/constructPayCorrectionRequest';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { CustomVizHookDoVizFn } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookDropdownOptions } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { Dialog } from '@headlessui/react';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { DropdownItem } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { errResult } from 'libs/end-to-end/src/result';
import { FilterableTableHeaderCell } from 'apps/sigma2-ui/src/components/ui/Table';
import { filterEmployeesByMinorityGender } from 'apps/sigma2-ui/src/pay-correction/libs/filters';
import { filterEmployeesByMinorityRace } from 'apps/sigma2-ui/src/pay-correction/libs/filters';
import { FlatCard } from 'apps/sigma2-ui/src/components/ui/FlatCard';
import { GenerateShareLinkRequest } from 'libs/end-to-end/src/links';
import { GenerateShareLinkResult } from 'libs/end-to-end/src/links';
import { getDateAsYYYYMMDD } from 'libs/end-to-end/src/dates';
import { getNewItems } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { getPayCorrectionAdjustedSalaryByEmployeeId } from 'apps/sigma2-ui/src/pay-correction/libs/getPayCorrectionAdjustedSalaryByEmployeeId';
import { getPayGradeGraphData } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { IconBarChartSvg } from 'apps/sigma2-ui/src/components/svg/IconBarChart';
import { IconCircularArrowWithDotSvg } from 'apps/sigma2-ui/src/components/svg/IconCircularArrowWithDot';
import { IconRadarChartSvg } from 'apps/sigma2-ui/src/components/svg/IconRadarChart';
import { IconShareSvg } from 'apps/sigma2-ui/src/components/svg/IconShare';
import { isEqual } from 'lodash';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { mapItems } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { okResult } from 'libs/end-to-end/src/result';
import { PayCorrectionAdjustedEmployeeSalaries } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionAdjustedEmployeeSalaries';
import { PayCorrectionAnalysisCards } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionAnalysisCards';
import { PayCorrectionChartDimensionSelect } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionChartDimensionSelect';
import { PayCorrectionChartTypes } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionChartType';
import { PayCorrectionDepartmentToJobLevelsMap } from 'apps/sigma2-ui/src/pay-correction/hooks/usePayCorrectionDepartmentToJobLevelsMap';
import { PayCorrectionDisplayType } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionDisplayType';
import { PayCorrectionDisplayTypes } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionDisplayType';
import { PayCorrectionEmployeeItem } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionTable';
import { PayCorrectionHeader } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionHeader';
import { PayCorrectionOutput } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionOutput';
import { payCorrectionPersistedMetricCacheKey } from 'apps/sigma2-ui/src/constants/usable-cache-keys';
import { PayCorrectionRequest } from 'libs/end-to-end/src/custom-viz/PayCorrectionTypes';
import { PayCorrectionResult } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionOutput';
import { PayCorrectionSidebar } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionSidebar';
import { PayCorrectionState } from 'apps/sigma2-ui/src/pay-correction/hooks/usePayCorrection';
import { PayCorrectionTable } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionTable';
import { PayGradeGraph } from 'apps/sigma2-ui/src/pay-correction/components/PayGradeGraph';
import { Result } from 'libs/end-to-end/src/result';
import { roundToStep } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';
import { SaveAltOutlined } from '@mui/icons-material';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { SetterWithReducer } from 'libs/end-to-end/src/ui-types';
import { ShareLinkModal } from 'apps/sigma2-ui/src/components/ui/ShareLinkModal';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Transition } from '@headlessui/react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { unparse } from 'papaparse';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useBlocker } from 'react-router';
import { useCheckboxGroup } from 'apps/sigma2-ui/src/components/ui/CheckboxGroup';
import { useCopilotCustomVizAutoloader } from 'apps/sigma2-ui/src/lib/hooks/useCopilotCustomVizAutoloader';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { useDynamicDimensions } from 'apps/sigma2-ui/src/lib/hooks/useDynamicDimensions';
import { useDynamicMetrics } from 'apps/sigma2-ui/src/lib/hooks/useDynamicMetrics';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { usePayCorrectionBudgetNeeded } from 'apps/sigma2-ui/src/pay-correction/hooks/usePayCorrectionBudgetNeeded';
import { usePayCorrectionBudgetRecommended } from 'apps/sigma2-ui/src/pay-correction/hooks/usePayCorrectionBudgetRecommended';
import { usePayCorrectionChartData } from 'apps/sigma2-ui/src/pay-correction/hooks/usePayCorrectionChartData';
import { usePayCorrectionDepartmentToJobLevelsMap } from 'apps/sigma2-ui/src/pay-correction/hooks/usePayCorrectionDepartmentToJobLevelsMap';
import { usePayCorrectionNeedsAttentionEmployees } from 'apps/sigma2-ui/src/pay-correction/hooks/usePayCorrectionNeedsAttentionEmployees';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { useShouldShowRadarChart } from 'apps/sigma2-ui/src/lib/hooks/useShouldShowRadarChart';
import { useSlider } from 'apps/sigma2-ui/src/lib/hooks/useSlider';
import { useUnloadEffect } from 'apps/sigma2-ui/src/lib/hooks/useUnloadEffect';
import { csvUnparse } from 'libs/end-to-end/src/csv-unparse';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';

const PAY_GRADE_GRAPH_STEP = 10_000;

// eslint-disable-next-line complexity
export const PayCorrectionInterface = ({
  userAuthenticatedApiCall,
  observation,
  organizationId,
  calibrationFraming,
}: {
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  observation: MyObservation;
  organizationId: string;
  calibrationFraming: CalibrationFraming;
}) => {
  const language = useLanguage();
  const context = useCustomViz();
  const { status, doViz, result, dropdownOptions } = context;

  const displayType = useDisplayType({
    allowedDisplayTypes: PayCorrectionDisplayTypes,
  });

  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedJobLevels, setSelectedJobLevels] = useState<string[]>([]);

  const isAcceptableDisplayType = displayType === 'acceptable';
  const isNeedsAttentionDisplayType = displayType === 'needs-attention';
  const isAllEmployeesDisplayType = displayType === 'all-employees';
  const isListDisplayType =
    isAcceptableDisplayType ||
    isNeedsAttentionDisplayType ||
    isAllEmployeesDisplayType;
  const isChartDisplayType = !isListDisplayType;

  const payCorrectionOutput = usePayCorrectionOutput({
    result,
  });

  const showError = useMemo(() => {
    return status === 'fatal-error' || context.result.resultType === 'err';
  }, [context.result.resultType, status]);

  const showLoading = useMemo(() => {
    return checkLoadingStatus({ status });
  }, [status]);

  const canAnalyze = useMemo(() => {
    if (isChartDisplayType) {
      return checkCanAnalyze({
        status,
        filters: [selectedDepartments, selectedLocations],
      });
    } else {
      return checkCanAnalyze({
        status,
        filters: [selectedDepartments, selectedLocations, selectedJobLevels],
      });
    }
  }, [
    isChartDisplayType,
    selectedDepartments,
    selectedJobLevels,
    selectedLocations,
    status,
  ]);

  const { allLevels, allDimensions } = useDimensionConfig();

  const dynamicDimensionsPayload = useDynamicDimensions(
    useMemo(() => {
      return {
        type: 'single',
        automaticallySelectFirstPreferred: true,
      };
    }, []),
  );

  const [adjustedSalaries, setAdjustedSalaries] =
    useState<PayCorrectionAdjustedEmployeeSalaries>({});

  const [currentShareLink, setCurrentShareLink] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const [uncheckedEmployees, setUncheckedEmployees] = useState<string[]>([]);

  const dynamicMetricsPayload = useDynamicMetrics(
    useMemo(() => {
      return {
        focusArea: 'Compensation',
        type: 'single',
        automaticallySelectFirstPreferred: true,
        persistKey: payCorrectionPersistedMetricCacheKey({
          webCacheKeyPrefix: observation.webCacheKeyPrefix,
        }),
      };
    }, [observation]),
  );

  const onApplyFilters = usePayCorrectionApplyFilters({
    canAnalyze,
    doViz,
    selectedDepartments,
    selectedJobLevels,
    selectedLocations,
    observation: observation.type,
    organizationId,
    adjustedSalaries,
    uncheckedEmployees,
    metric: dynamicMetricsPayload.selectedMetric,
  });

  const currentCustomVizRequestToExecute =
    usePayCorrectionCurrentCustomVizRequestToExecute({
      organizationId,
      observation: observation.type,
      selectedDepartments,
      selectedJobLevels,
      selectedLocations,
      adjustedSalaries,
      uncheckedEmployees,
      dimension: dynamicDimensionsPayload.selectedDimension,
      metric: dynamicMetricsPayload.selectedMetric,
    });

  const currentState = usePayCorrectionCurrentState({
    result,
    status,
  });

  const { generateShareLink, lastShareDetails } =
    usePayCorrectionGenerateShareLink({
      currentState,
      userAuthenticatedApiCall,
      request: currentCustomVizRequestToExecute,
      uncheckedEmployees,
    });

  const onShare = useCallback(() => {
    generateShareLink((link) => {
      setCurrentShareLink(link);
      setShowShareModal(true);
    });
  }, [generateShareLink]);

  const [shouldPrefillFilters, setShouldPrefillFilters] = useState(false);

  const resetFiltersToInitialState =
    usePayCorrectionUpdateDropdownOptionsEffect({
      dropdownOptions,
      setSelectedDepartments,
      setSelectedLocations,
      setSelectedJobLevels,
      shouldPrefillFilters,
    });

  const onAnalyse = useCallback(() => {
    onApplyFilters(dynamicDimensionsPayload.selectedDimension);
  }, [dynamicDimensionsPayload, onApplyFilters]);

  const {
    onSelectSingle: onSelectSingleDimension,
    resetDimensionsToInitialState,
  } = dynamicDimensionsPayload;

  const { onSelectSingle: onSelectSingleMetric, resetMetricsToInitialState } =
    dynamicMetricsPayload;

  const setSelections = useCallback(
    (selections: PayCorrectionRequest | null) => {
      if (!selections) {
        return;
      }

      setSelectedDepartments(selections.inputs.dropdowns.department);
      setSelectedJobLevels(selections.inputs.dropdowns.job_level ?? []);
      setSelectedLocations(selections.inputs.dropdowns.location);
      setUncheckedEmployees([]);
      setAdjustedSalaries(selections.inputs.extras.adjusted_salaries);

      onSelectSingleDimension(selections.inputs.extras.dimension);
      onSelectSingleMetric(selections.inputs.extras.metric);
    },
    [onSelectSingleDimension, onSelectSingleMetric],
  );

  const onAnalyseWithRequest = useCallback(
    (request: PayCorrectionRequest) => {
      doViz(request);
    },
    [doViz],
  );

  const { shareLinkRequest, onUserAnalysisCancel } =
    useCopilotCustomVizAutoloader({
      canAnalyze,
      onAnalyze: onAnalyse,
      onAnalyseWithRequest,
      context,
      lastPersistedRequest: null,
      setSelections,
      setShouldPrefillFilters,
      shouldPrefillFilters,
      type: PayCorrectionRequest,
      resetFiltersToInitialState,
      resetMetricsToInitialState,
      resetDimensionsToInitialState,
    });

  const imageRef = React.useRef<HTMLInputElement | null>(null);

  const departmentToJobLevelsMap = usePayCorrectionDepartmentToJobLevelsMap({
    dropdownOptions,
  });

  const {
    checkboxes: dimensionsCheckboxes,
    selectAllCheckbox: dimensionsSelectAllCheckbox,
    onChange: onDimensionsChange,
    onSelectAllChange: onDimensionsSelectAllChange,
  } = useCheckboxGroup({
    initialSelectAllCheckbox: {
      checked: true,
      children: 'Select all',
    },
    initialCheckboxes: allLevels.map((level) => {
      return {
        id: level.columnValue,
        children: level.displayName,
        checked: true,
      };
    }),
  });

  const activeLevels = dimensionsCheckboxes.reduce((acc, checkbox) => {
    if (checkbox.checked) {
      acc.add(checkbox.id);
    }

    return acc;
  }, new Set<string>());

  // Used for filtering the table items when selecting a pay grade bubble
  const [adjustedSalaryFilters, setPayGroupBubbleFilters] = useState<
    number[] /* adjustedSalaries */
  >([]);

  const togglePayGroupBubbleFilter = useCallback(
    (adjustedSalaryGroup: number) => {
      setPayGroupBubbleFilters((currentFilters) => {
        if (currentFilters.includes(adjustedSalaryGroup)) {
          return currentFilters.filter(
            (filter) => filter !== adjustedSalaryGroup,
          );
        }

        return [...currentFilters, adjustedSalaryGroup];
      });
    },
    [setPayGroupBubbleFilters],
  );

  const items = usePayCorrectionItems({
    payCorrectionOutput,
    displayType,
    jobLevel: selectedJobLevels.length === 1 ? selectedJobLevels[0] : null,
    activeLevels,
  });

  const jobLevelDropdownItems = usePayCorrectionJobLevelDropdownItems({
    departmentToJobLevelsMap,
    selectedDepartments,
  });

  const itemsWithAdjustedSalaries = useMemo(() => {
    return items.map((i) => {
      return {
        ...i,
        adjusted_salary: getPayCorrectionAdjustedSalaryByEmployeeId({
          adjustedSalaries,
          employeeid: i.employeeid,
          recommendedSalary: i.recommended_salary,
        }),
      };
    });
  }, [adjustedSalaries, items]);

  // These are filtered by the pay grade graph filters.
  const tableItems = useMemo(
    () =>
      itemsWithAdjustedSalaries.filter((item) => {
        const noFilters = adjustedSalaryFilters.length === 0;

        const matchesFilter = adjustedSalaryFilters.includes(
          roundToStep({
            value: item.adjusted_salary,
            step: PAY_GRADE_GRAPH_STEP,
          }),
        );

        return noFilters || matchesFilter;
      }),
    [adjustedSalaryFilters, itemsWithAdjustedSalaries],
  );

  const downloadItems = useMemo(() => {
    return itemsWithAdjustedSalaries.filter((i) => {
      return !uncheckedEmployees.includes(i.employeeid);
    });
  }, [itemsWithAdjustedSalaries, uncheckedEmployees]);

  const canDownload = useMemo(() => {
    return downloadItems.length > 0;
  }, [downloadItems]);

  const onDownloadClick = useCallback(() => {
    if (!canDownload) {
      return;
    }

    const unparseResult = csvUnparse(downloadItems);

    if (unparseResult.resultType === 'err') {
      return;
    }

    downloadCsv({
      csv: unparseResult.value,
      fileName: `PayCorrection-${getDateAsYYYYMMDD(new Date())}`,
    });
  }, [canDownload, downloadItems]);

  const allItemsAreFromSameJobLevel = useMemo(() => {
    if (items.length === 0) {
      return false;
    }

    const firstJobLevel = items[0].job_level;

    return items.every((i) => i.job_level === firstJobLevel);
  }, [items]);

  // Used for highlighting the dimension in the pay grade graph
  const [highlightedDimensionLevel, setHighlightedDimensionLevel] = useState<
    string /* dimensionConfig.columnValue */ | null
  >(null);

  const payGradeGraph = useMemo(() => {
    // check that all items have the same job level
    if (!allItemsAreFromSameJobLevel) {
      return null;
    }

    const rawInternalGoalMin =
      payCorrectionOutput?.internal_pay_benchmarking_pay_band?.min ?? null;

    const rawInternalGoalMax =
      payCorrectionOutput?.internal_pay_benchmarking_pay_band?.max ?? null;

    const {
      data,
      roundedGraphMin,
      roundedGraphMax,
      roundedInternalGoalMin,
      roundedInternalGoalMax,
    } = getPayGradeGraphData({
      employees: itemsWithAdjustedSalaries,
      step: PAY_GRADE_GRAPH_STEP,
      internalGoalMin: rawInternalGoalMin,
      internalGoalMax: rawInternalGoalMax,
    });

    return (
      <PayGradeGraph
        data={data}
        employeeCount={itemsWithAdjustedSalaries.length}
        roundedGraphMin={roundedGraphMin}
        roundedGraphMax={roundedGraphMax}
        roundedInternalGoalMin={roundedInternalGoalMin}
        roundedInternalGoalMax={roundedInternalGoalMax}
        onAdjustedSalaryFilterChange={togglePayGroupBubbleFilter}
        highlightedDimensionLevel={highlightedDimensionLevel}
        adjustedSalaryFilters={adjustedSalaryFilters}
        step={PAY_GRADE_GRAPH_STEP}
      />
    );
  }, [
    allItemsAreFromSameJobLevel,
    payCorrectionOutput,
    highlightedDimensionLevel,
    togglePayGroupBubbleFilter,
    adjustedSalaryFilters,
    itemsWithAdjustedSalaries,
  ]);

  const { resetAdjustedSalaries, resetUncheckedEmployees } = useMemo(() => {
    return {
      resetAdjustedSalaries:
        shareLinkRequest && shareLinkRequest.customVizId === 'payCorrection'
          ? shareLinkRequest.inputs.extras.adjusted_salaries
          : {},
      resetUncheckedEmployees: [],
    };
  }, [shareLinkRequest]);

  const onReset = useCallback(() => {
    setUncheckedEmployees(resetUncheckedEmployees);
    setAdjustedSalaries(resetAdjustedSalaries);
  }, [resetAdjustedSalaries, resetUncheckedEmployees]);

  const { compareUncheckedEmployees, compareAdjustedSalaries } = useMemo(() => {
    if (lastShareDetails) {
      return {
        compareUncheckedEmployees: lastShareDetails.uncheckedEmployees,
        compareAdjustedSalaries: lastShareDetails.adjustedSalaries,
      };
    }
    return {
      compareUncheckedEmployees: resetUncheckedEmployees,
      compareAdjustedSalaries: resetAdjustedSalaries,
    };
  }, [lastShareDetails, resetUncheckedEmployees, resetAdjustedSalaries]);

  return (
    <div className="relative" ref={imageRef}>
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />

      <PayCorrectionSavedChangesTracker
        uncheckedEmployees={uncheckedEmployees}
        compareUncheckedEmployees={compareUncheckedEmployees}
        adjustedSalaries={adjustedSalaries}
        compareAdjustedSalaries={compareAdjustedSalaries}
      />
      {showShareModal && (
        <ShareLinkModal
          shareLink={currentShareLink ?? ''}
          showModal={showShareModal}
          setShowModal={setShowShareModal}
        />
      )}
      {showLoading && (
        <div className="absolute z-cover flex items-center justify-center w-full h-screen bg-gray-100 bg-opacity-80">
          <CustomVizCircularSpinner
            onUserAnalysisCancel={onUserAnalysisCancel}
          />
        </div>
      )}
      <Spacing direction="vertical" size="sm" className="my-4">
        {isListDisplayType && (
          <>
            <BreadCrumbGroup>
              <BreadCrumbItem to="/copilot">Co-Pilot</BreadCrumbItem>

              <BreadCrumbItem to="/copilot/pay-correction">
                Correction
              </BreadCrumbItem>

              <BreadCrumbItem to={`/copilot/pay-correction/${displayType}`}>
                {language.payCorrectionDisplayTypeTitle({
                  displayType,
                  language,
                })}
              </BreadCrumbItem>
            </BreadCrumbGroup>

            <Heading level={2} fontFamily="font-sans" fontWeight="font-bold">
              {language.payCorrectionDisplayTypeTitle({
                displayType,
                language,
              })}
            </Heading>
          </>
        )}

        <DataRefreshIndicator
          status={context.status}
          resultCreatedAt={context.resultCreatedAt}
          heightClass={'h-auto'}
        />

        <Spacing direction="horizontal" size="md">
          <FlatCard className="shadow-2px bg-gray-100 w-full">
            <Spacing direction="horizontal" size="sm" className="w-full">
              <SelectBoxWithSearch
                type="single"
                setSelected={() => {}}
                selected={'internal'}
                list={[
                  {
                    optionValue: 'internal',
                    optionLabel: 'Internal Market',
                  },
                ]}
                label="Benchmark"
                widthClass="w-full"
              />

              <SelectBoxWithSearch
                type="multi"
                label="Location"
                setSelected={setSelectedLocations}
                selected={selectedLocations}
                list={mapItems({
                  options: context.dropdownOptions,
                  dropdownId: 'location',
                })}
                widthClass="w-full"
              />

              <SelectBoxWithSearch
                type="multi"
                label="Department"
                setSelected={setSelectedDepartments}
                selected={selectedDepartments}
                list={mapItems({
                  options: context.dropdownOptions,
                  dropdownId: 'department',
                })}
                widthClass="w-full"
              />

              <SelectBoxWithSearch
                type="multi"
                setSelected={setSelectedJobLevels}
                selected={selectedJobLevels}
                list={jobLevelDropdownItems}
                label="Level"
                widthClass="w-full"
              />

              <div>
                <LegacyButton
                  data-qa="calculate-btn"
                  disabled={!canAnalyze}
                  variant={LegacyButtonVariant.APPLY_BUTTON}
                  onClick={() =>
                    onApplyFilters(dynamicDimensionsPayload.selectedDimension)
                  }
                  className="px-10 h-full"
                >
                  Apply
                </LegacyButton>
              </div>
            </Spacing>
          </FlatCard>

          <FlatCard className="bg-gray-50 flex items-center">
            <Spacing
              direction="horizontal"
              size="md"
              className={cn(
                showError && 'text-gray-600',
                !showError && 'text-secondary',
              )}
            >
              <button onClick={onShare} disabled={showError}>
                <IconShareSvg
                  svg={{ className: 'w-6 h-6' }}
                  wantsCurrentColor
                />
              </button>

              <button onClick={onReset} disabled={showError}>
                <IconCircularArrowWithDotSvg
                  svg={{ className: 'w-6 h-6' }}
                  wantsCurrentColor
                />
              </button>

              <button
                type="button"
                onClick={onDownloadClick}
                disabled={showError || !canDownload}
                aria-label="Download CSV"
              >
                <SaveAltOutlined
                  className={cn(
                    'w-6 h-6',
                    showError && 'text-gray-600',
                    !showError && 'text-secondary',
                  )}
                />
              </button>
            </Spacing>
          </FlatCard>
        </Spacing>

        {showError && (
          <div className="flex items-center justify-center w-full h-full pb-10 bg-gray-100 bg-opacity-80 pr-7">
            <Alert severity="error">
              There was an error loading this data.
            </Alert>
          </div>
        )}

        {isChartDisplayType && payCorrectionOutput && (
          <div className="pt-8">
            <PayCorrectionChart
              payCorrectionOutput={payCorrectionOutput}
              selectedDimension={dynamicDimensionsPayload.selectedDimension}
              setSelectedDimension={dynamicDimensionsPayload.onSelectSingle}
              allDimensions={allDimensions}
              adjustedSalaries={adjustedSalaries}
              items={itemsWithAdjustedSalaries}
              onAnalyse={onApplyFilters}
              calibrationFraming={calibrationFraming}
            />
          </div>
        )}

        {isListDisplayType && (
          <div className="pt-8">
            <PayCorrectionListView
              listItems={tableItems}
              payGradeGraph={payGradeGraph}
              setAdjustedSalaries={setAdjustedSalaries}
              adjustedSalaries={adjustedSalaries}
              uncheckedEmployees={uncheckedEmployees}
              setUncheckedEmployees={setUncheckedEmployees}
              selectedDimensionLevelFilter={highlightedDimensionLevel}
              // We can only filter by dimension if the pay grade graph is visible
              onDimensionLevelFilterChange={
                payGradeGraph ? setHighlightedDimensionLevel : null
              }
              dimensionTableHeaderCell={
                <FilterableTableHeaderCell
                  selectAllCheckbox={dimensionsSelectAllCheckbox}
                  checkboxes={dimensionsCheckboxes}
                  onSelectAllChange={onDimensionsSelectAllChange}
                  onChange={onDimensionsChange}
                >
                  Dimensions
                </FilterableTableHeaderCell>
              }
            />
          </div>
        )}
      </Spacing>
    </div>
  );
};

export const useDisplayType = <T extends string>({
  allowedDisplayTypes,
}: {
  allowedDisplayTypes: T[];
}): T | null => {
  const { displayType: displayTypeRaw } = useParams<{ displayType: string }>();

  return useMemo(() => {
    const displayType = allowedDisplayTypes.find(
      (allowedDisplayType) => allowedDisplayType === displayTypeRaw,
    );

    if (displayType === undefined) {
      return null;
    }

    return displayType;
  }, [displayTypeRaw, allowedDisplayTypes]);
};

export const usePayCorrectionOutput = ({
  result,
}: {
  result: CustomVizHookResult;
}): PayCorrectionOutput | null => {
  return useMemo(() => {
    return result.resultType === 'ok' && result.value
      ? PayCorrectionOutput.parse(result.value)
      : null;
  }, [result]);
};

export const usePayCorrectionUpdateDropdownOptionsEffect = ({
  setSelectedDepartments,
  setSelectedLocations,
  setSelectedJobLevels,
  dropdownOptions,
  shouldPrefillFilters,
}: {
  setSelectedDepartments: Setter<string[]>;
  setSelectedLocations: Setter<string[]>;
  setSelectedJobLevels: Setter<string[]>;
  dropdownOptions: CustomVizHookDropdownOptions;
  shouldPrefillFilters: boolean;
}) => {
  const handler = useCallback(() => {
    setSelectedDepartments(
      getNewItems({
        options: dropdownOptions,
        dropdownId: 'department',
      }),
    );
    setSelectedLocations(
      getNewItems({
        options: dropdownOptions,
        dropdownId: 'location',
      }),
    );
    setSelectedJobLevels(
      getNewItems({
        options: dropdownOptions,
        dropdownId: 'job_level',
      }),
    );
  }, [
    dropdownOptions,
    setSelectedDepartments,
    setSelectedLocations,
    setSelectedJobLevels,
  ]);

  useEffect(() => {
    if (!shouldPrefillFilters) {
      return;
    }
    handler();
  }, [handler, shouldPrefillFilters]);

  return handler;
};

const chartTypeButtonClassName = ({ active }: { active: boolean }) =>
  cn('h-fit rounded-lg p-1', {
    'bg-highlight': active,
    'bg-transparent': !active,
  });

const chartTypeIconClassName = ({ active }: { active: boolean }) =>
  cn('w-8 h-8', {
    'text-secondary ': active,
    'text-gray-800': !active,
  });

export const PayCorrectionChart = ({
  payCorrectionOutput,
  items,
  adjustedSalaries,
  selectedDimension,
  setSelectedDimension,
  allDimensions,
  onAnalyse,
  calibrationFraming,
}: {
  payCorrectionOutput: PayCorrectionOutput;
  items: PayCorrectionEmployeeItem[];
  adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
  selectedDimension: string | null;
  setSelectedDimension: Setter<string | null>;
  allDimensions: DimensionDefinition[];
  onAnalyse: (dimension: string) => void;
  calibrationFraming: CalibrationFraming;
}) => {
  const navigate = useNavigateSafe();
  const language = useLanguage();

  const [chartType, selectChartType] = useSlider({
    options: PayCorrectionChartTypes,
    initialSelectedOption: 'bar',
  });

  const chartData = usePayCorrectionChartData({
    payCorrectionOutput,
    calibrationFraming,
  });

  const needsAttentionItems = usePayCorrectionNeedsAttentionEmployees({
    listItems: items,
  });

  const numOfEmployees = items.length;
  const numOfUnderpaid = needsAttentionItems.length;

  const currentPayGapPercentage = calcPayGapGroup({
    logger: uiAppLogger,
    employees: needsAttentionItems.map((emp) => {
      return {
        marketSalary: emp.recommended_salary,
        adjustedSalary: emp.salary,
      };
    }),
  });

  const projectedPayPercentage = calcPayGapGroup({
    logger: uiAppLogger,
    employees: needsAttentionItems.map((emp) => {
      const adjustedSalary = getPayCorrectionAdjustedSalaryByEmployeeId({
        adjustedSalaries,
        employeeid: emp.employeeid,
        recommendedSalary: emp.recommended_salary,
      });

      return {
        adjustedSalary,
        marketSalary: emp.recommended_salary,
      };
    }),
  });

  const budgetNeeded = usePayCorrectionBudgetNeeded({
    adjustedSalaries,
    listItems: items,
  });

  const budgetRecommended = usePayCorrectionBudgetRecommended({
    listItems: items,
  });

  const shouldShowRadarChart = useShouldShowRadarChart(chartData.length);

  return (
    <Spacing direction="vertical" size="lg" className="items-center w-full">
      <Spacing
        direction="horizontal"
        size="md"
        className="justify-between w-full"
      >
        <PayCorrectionSidebar
          numEmployees={numOfEmployees}
          payGapPercentage={currentPayGapPercentage}
          compensation={{
            numAcceptable: numOfEmployees - numOfUnderpaid,
            numNeedsAttention: numOfUnderpaid,
          }}
          budget={{
            actual: budgetNeeded,
            recommended: budgetRecommended,
          }}
          impact={{
            payGapPercentage: projectedPayPercentage,
          }}
          onAcceptableListCardClick={() => {
            navigate(`/copilot/pay-correction/acceptable`);
          }}
          onNeedsAttentionListCardClick={() => {
            navigate(`/copilot/pay-correction/needs-attention`);
          }}
          onAllEmployeesListCardClick={() => {
            navigate(`/copilot/pay-correction/all-employees`);
          }}
          todaysPayGapTooltipContents={language.payCorrectionTodaysPayGapTooltip()}
        />
        <div className="w-full max-w-[590vw] flex flex-col gap-4 justify-center items-center">
          <PayCorrectionHeader
            biasType={
              <PayCorrectionChartDimensionSelect
                selectedDimension={selectedDimension}
                allDimensions={allDimensions}
                setSelectedDimension={setSelectedDimension}
                onAnalyse={onAnalyse}
              />
            }
            chartType={
              <Spacing direction="horizontal" size="sm">
                {shouldShowRadarChart && (
                  <>
                    <button
                      type="button"
                      onClick={() => selectChartType('radar')}
                      aria-label="View bar chart"
                      className={chartTypeButtonClassName({
                        active: 'radar' === chartType,
                      })}
                    >
                      <IconRadarChartSvg
                        wantsCurrentColor
                        svg={{
                          className: chartTypeIconClassName({
                            active: 'radar' === chartType,
                          }),
                        }}
                      />
                    </button>

                    <button
                      type="button"
                      onClick={() => selectChartType('bar')}
                      aria-label="View radar chart"
                      className={chartTypeButtonClassName({
                        active: 'bar' === chartType,
                      })}
                    >
                      <IconBarChartSvg
                        wantsCurrentColor
                        svg={{
                          className: chartTypeIconClassName({
                            active: 'bar' === chartType,
                          }),
                        }}
                      />
                    </button>
                  </>
                )}
              </Spacing>
            }
            calibrationFraming={calibrationFraming}
          />

          <div className="w-full rounded-lg max-w-[600px]">
            {chartType === 'radar' && shouldShowRadarChart ? (
              <CalibrationLevelRadarChart
                dataPoints={chartData}
                calibrationFraming={calibrationFraming}
                dataTestId={'payCorrectionRadarChart'}
              />
            ) : (
              <CalibrationLevelBarChart
                dataPoints={chartData}
                calibrationFraming={calibrationFraming}
              />
            )}
          </div>

          <div>
            <S2Typography
              variant="body-7"
              translatedContent={language.payCorrectionGraphExplanation({
                calibrationFraming,
              })}
            />
          </div>
        </div>
      </Spacing>
    </Spacing>
  );
};

export const usePayCorrectionApplyFilters = ({
  canAnalyze,
  doViz,
  selectedDepartments,
  selectedJobLevels,
  selectedLocations,
  observation,
  organizationId,
  adjustedSalaries,
  uncheckedEmployees,
  metric,
}: {
  canAnalyze: boolean;
  doViz: CustomVizHookDoVizFn;
  selectedDepartments: string[];
  selectedJobLevels: string[];
  selectedLocations: string[];
  observation: ObservationLocationType;
  organizationId: string;
  adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
  uncheckedEmployees: string[];
  metric: string | null;
}) => {
  return useCallback(
    (dimension: string | null) => {
      if (!canAnalyze || !dimension || !metric) {
        return;
      }

      const customVizRequest = constructPayCorrectionRequest({
        departments: selectedDepartments,
        jobLevels: selectedJobLevels,
        locations: selectedLocations,
        observation,
        organizationId,
        adjustedSalaries,
        uncheckedEmployees,
        metric,
        dimension,
      });

      doViz(customVizRequest);
    },
    [
      canAnalyze,
      metric,
      selectedDepartments,
      selectedJobLevels,
      selectedLocations,
      observation,
      organizationId,
      adjustedSalaries,
      uncheckedEmployees,
      doViz,
    ],
  );
};

export const usePayCorrectionCurrentCustomVizRequestToExecute = ({
  organizationId,
  observation,
  selectedDepartments,
  selectedLocations,
  selectedJobLevels,
  adjustedSalaries,
  uncheckedEmployees,
  metric,
  dimension,
}: {
  organizationId: string;
  observation: ObservationLocationType;
  selectedDepartments: string[];
  selectedLocations: string[];
  selectedJobLevels: string[];
  adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
  uncheckedEmployees: string[];
  metric: string | null;
  dimension: string | null;
}): PayCorrectionRequest | null => {
  return useMemo(() => {
    if (!dimension || !metric) {
      return null;
    }
    return constructPayCorrectionRequest({
      departments: selectedDepartments,
      jobLevels: selectedJobLevels,
      locations: selectedLocations,
      observation,
      organizationId,
      adjustedSalaries,
      uncheckedEmployees,
      metric,
      dimension,
    });
  }, [
    dimension,
    metric,
    selectedDepartments,
    selectedJobLevels,
    selectedLocations,
    observation,
    organizationId,
    adjustedSalaries,
    uncheckedEmployees,
  ]);
};

export const usePayCorrectionCurrentState = ({
  status,
  result,
}: {
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
}): PayCorrectionState => {
  return useMemo<PayCorrectionState>(() => {
    if (result.resultType === 'err') {
      return {
        state: 'error_state',
      };
    }

    if (status === 'loading-viz') {
      return {
        state: 'loading_state',
      };
    }

    if (result.value === null) {
      return {
        state: 'empty_state',
      };
    }

    const parseResult = PayCorrectionOutput.safeParse(result.value);
    if (parseResult.success) {
      return {
        state: 'success',
        output: parseResult.data,
      };
    } else {
      return {
        state: 'error_state',
      };
    }
  }, [result, status]);
};

export interface PayCorrectionGenerateShareLinkHook {
  generateShareLink: (onComplete: (shareLink: string) => void) => void;
  lastShareDetails: {
    adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
    uncheckedEmployees: string[];
  } | null;
}
export const usePayCorrectionGenerateShareLink = ({
  currentState,
  userAuthenticatedApiCall,
  request,
  uncheckedEmployees,
}: {
  currentState: PayCorrectionState;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  request: PayCorrectionRequest | null;
  uncheckedEmployees: string[];
}): PayCorrectionGenerateShareLinkHook => {
  const [lastShareDetails, setLastShareDetails] = useState<{
    adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
    uncheckedEmployees: string[];
  } | null>(null);

  const generateShareLink = useAsyncCallback(
    async (isAlive, onComplete: (shareLink: string) => void) => {
      uiAppLogger.info('starting_share');
      if (!isAlive() || !request) {
        return;
      }

      if (currentState.state !== 'success') {
        uiAppLogger.info('output_type', currentState.state);
        return;
      }

      uiAppLogger.info('successful output');

      const shareLinkRequestBody: GenerateShareLinkRequest = {
        baseUrl: window.location.origin,
        request,
      };

      uiAppLogger.info(shareLinkRequestBody);

      const generateShareLinkResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/share/generate`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(shareLinkRequestBody),
        },
        responseSchema: GenerateShareLinkResult,
      });
      if (
        !generateShareLinkResult ||
        generateShareLinkResult.resultType === 'err'
      ) {
        return;
      }

      setLastShareDetails({
        adjustedSalaries: request.inputs.extras.adjusted_salaries,
        uncheckedEmployees: uncheckedEmployees,
      });

      const link = generateShareLinkResult.value.shareLink;
      onComplete(link);
    },
    [currentState.state, request, uncheckedEmployees, userAuthenticatedApiCall],
  );

  return {
    generateShareLink,
    lastShareDetails,
  };
};

/**
 * We filter out dimensions that are not active in the filters shown in the dimensions table header.
 */
const hasAnyActiveDimensions = ({
  item,
  activeLevels,
}: {
  item: PayCorrectionResult;
  activeLevels: Set<string>;
}): boolean => {
  const noGender = !item.gender;
  const noRace = !item.race;

  const hasActiveGenderLevel =
    (item.gender && activeLevels.has(item.gender)) || noGender;

  const hasActiveRaceLevel =
    (item.race && activeLevels.has(item.race)) || noRace;

  return hasActiveGenderLevel || hasActiveRaceLevel;
};

export const usePayCorrectionItems = ({
  payCorrectionOutput,
  displayType,
  jobLevel,
  activeLevels,
}: {
  displayType: PayCorrectionDisplayType | null;
  jobLevel: string | null;
  payCorrectionOutput: PayCorrectionOutput | null;
  activeLevels: Set<string>;
}): PayCorrectionEmployeeItem[] => {
  return useMemo(() => {
    const itemsWithAdjustedSalaries =
      payCorrectionOutput?.pay_correction_results.map((item) => {
        return {
          ...item,
          id: item.employeeid,
          checked: false,
        };
      }) || [];

    const filteredItems = itemsWithAdjustedSalaries.filter((item) => {
      return hasAnyActiveDimensions({
        activeLevels,
        item,
      });
    });

    if (
      displayType === null ||
      displayType === 'chart' ||
      displayType === 'all-employees'
    ) {
      return filteredItems;
    }

    if (displayType === 'pay-grade') {
      return filteredItems.filter((item) => item.job_level === jobLevel);
    }

    if (displayType === 'needs-attention') {
      return filteredItems.filter(
        (item) => item.salary < item.recommended_salary,
      );
    }

    if (displayType === 'acceptable') {
      return filteredItems.filter(
        (item) => item.salary >= item.recommended_salary,
      );
    }

    return filteredItems;
  }, [payCorrectionOutput, displayType, jobLevel, activeLevels]);
};

export const PayCorrectionListView = ({
  listItems,
  payGradeGraph,
  setAdjustedSalaries,
  adjustedSalaries,
  dimensionTableHeaderCell,
  uncheckedEmployees,
  selectedDimensionLevelFilter,
  setUncheckedEmployees,
  onDimensionLevelFilterChange,
}: {
  listItems: PayCorrectionEmployeeItem[];
  payGradeGraph?: React.ReactNode;
  setAdjustedSalaries: SetterWithReducer<PayCorrectionAdjustedEmployeeSalaries>;
  adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
  dimensionTableHeaderCell: React.ReactNode;
  uncheckedEmployees: string[];
  selectedDimensionLevelFilter: string | null;
  setUncheckedEmployees: Setter<string[]>;
  onDimensionLevelFilterChange: Setter<string | null> | null;
}) => {
  const { getPrivilegedLevelForDimension, isMinorityDimensionLevel } =
    useDimensionConfig();
  const language = useLanguage();

  // TODO consider making this an object with employeeid as the key so we can avoid the O(n^2) lookup
  const checkedListItems = useMemo(
    () =>
      listItems.filter((item) => {
        return !uncheckedEmployees.includes(item.employeeid);
      }),
    [listItems, uncheckedEmployees],
  );

  /**
   * We only run the calculations if the employee is checked.
   */
  const minorityGenderEmployees = filterEmployeesByMinorityGender({
    items: checkedListItems,
    isMinorityDimensionLevel,
  });
  const minorityRaceEmployees = filterEmployeesByMinorityRace({
    items: checkedListItems,
    isMinorityDimensionLevel,
  });

  const projectedGenderPayGap = calcPayGapGroup({
    logger: uiAppLogger,
    employees: minorityGenderEmployees.map((emp) => {
      const adjustedSalary = getPayCorrectionAdjustedSalaryByEmployeeId({
        adjustedSalaries,
        employeeid: emp.employeeid,
        recommendedSalary: emp.recommended_salary,
      });

      return {
        adjustedSalary,
        marketSalary: emp.recommended_salary,
      };
    }),
  });

  const currentGenderPayGap = calcPayGapGroup({
    logger: uiAppLogger,
    employees: minorityGenderEmployees.map((emp) => {
      return {
        adjustedSalary: emp.salary,
        marketSalary: emp.recommended_salary,
      };
    }),
  });

  const projectedRacePayGap = calcPayGapGroup({
    logger: uiAppLogger,
    employees: minorityRaceEmployees.map((emp) => {
      const adjustedSalary = getPayCorrectionAdjustedSalaryByEmployeeId({
        adjustedSalaries,
        employeeid: emp.employeeid,
        recommendedSalary: emp.recommended_salary,
      });

      return {
        adjustedSalary,
        marketSalary: emp.recommended_salary,
      };
    }),
  });

  const currentRacePayGap = calcPayGapGroup({
    logger: uiAppLogger,
    employees: minorityRaceEmployees.map((emp) => {
      return {
        adjustedSalary: emp.salary,
        marketSalary: emp.recommended_salary,
      };
    }),
  });

  const budgetNeeded = usePayCorrectionBudgetNeeded({
    adjustedSalaries,
    listItems: checkedListItems,
  });

  const budgetRecommended = usePayCorrectionBudgetRecommended({
    listItems: checkedListItems,
  });

  return (
    <Spacing direction="vertical" size="xl">
      <PayCorrectionAnalysisCards
        budget={{
          needed: budgetNeeded,
          recommended: budgetRecommended,
        }}
        genderPayGap={{
          projected: projectedGenderPayGap,
          current: currentGenderPayGap,
        }}
        racialPayGap={{
          projected: projectedRacePayGap,
          current: currentRacePayGap,
        }}
        language={language}
        getPrivilegedLevelForDimension={getPrivilegedLevelForDimension}
      />

      {payGradeGraph}

      <PayCorrectionTable
        // We pass the unchecked employees to the table since they're still rendered and the user can later check them
        items={listItems}
        isLoading={false}
        setAdjustedSalaries={setAdjustedSalaries}
        adjustedSalaries={adjustedSalaries}
        dimensionTableHeaderCell={dimensionTableHeaderCell}
        uncheckedEmployees={uncheckedEmployees}
        setUncheckedEmployees={setUncheckedEmployees}
        selectedDimensionLevelFilter={selectedDimensionLevelFilter}
        onDimensionLevelFilterChange={onDimensionLevelFilterChange}
      />
    </Spacing>
  );
};

export const usePayCorrectionJobLevelDropdownItems = ({
  selectedDepartments,
  departmentToJobLevelsMap,
}: {
  selectedDepartments: string[];
  departmentToJobLevelsMap: PayCorrectionDepartmentToJobLevelsMap;
}): DropdownItem[] => {
  return useMemo(() => {
    // A user can only select one or more job levels if they have selected one department.
    if (selectedDepartments.length !== 1) {
      return [];
    }

    uiAppLogger.json.info(departmentToJobLevelsMap);

    const department = selectedDepartments[0];
    const jobLevelMap = departmentToJobLevelsMap[department];

    if (!jobLevelMap) {
      return [];
    }

    return Object.entries(jobLevelMap).map(([value, text]) => {
      return {
        optionLabel: text,
        optionValue: value,
      };
    });
  }, [departmentToJobLevelsMap, selectedDepartments]);
};

export const downloadCsv = ({
  csv,
  fileName,
}: {
  csv: string;
  /** The name of the file to download WITHOUT the extension. */
  fileName: string;
}): void => {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.setAttribute('href', url);
  anchor.setAttribute('download', `${fileName}.csv`);
  anchor.click();
  anchor.remove();
};

const anyUnsavedChanges = ({
  uncheckedEmployees,
  compareUncheckedEmployees,
  adjustedSalaries,
  compareAdjustedSalaries,
}: {
  uncheckedEmployees: string[];
  compareUncheckedEmployees: string[];
  adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
  compareAdjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
}) => {
  const uncheckedEmployeesAreEqual = areStringArraysEqual(
    uncheckedEmployees,
    compareUncheckedEmployees,
  );

  const adjustedSalariesAreEqual = isEqual(
    adjustedSalaries,
    compareAdjustedSalaries,
  );

  return !uncheckedEmployeesAreEqual || !adjustedSalariesAreEqual;
};

export const PayCorrectionSavedChangesTracker = ({
  uncheckedEmployees,
  compareUncheckedEmployees,
  adjustedSalaries,
  compareAdjustedSalaries,
}: {
  uncheckedEmployees: string[];
  compareUncheckedEmployees: string[];
  adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
  compareAdjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
}) => {
  const hasUnsavedChanges = useMemo(() => {
    return anyUnsavedChanges({
      uncheckedEmployees,
      compareUncheckedEmployees,
      adjustedSalaries,
      compareAdjustedSalaries,
    });
  }, [
    adjustedSalaries,
    compareAdjustedSalaries,
    compareUncheckedEmployees,
    uncheckedEmployees,
  ]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (nextLocation?.pathname.startsWith('/copilot/pay-correction')) {
      return false;
    }

    if (currentLocation.pathname.startsWith('/copilot/pay-correction')) {
      return hasUnsavedChanges;
    }

    return false;
  });

  const resetBlocker = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const discardChanges = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  useUnloadEffect(
    (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        const confirmationMessage =
          'Are you sure you want to leave? You have unsaved changes. You may use the share button to save your changes.';

        event.preventDefault();
        event.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    },
    [hasUnsavedChanges],
  );

  return (
    <>
      {blocker && (
        <Transition appear show={blocker.state === 'blocked'} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-overlay"
            onClose={resetBlocker}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Unsaved changes
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You have unsaved changes to adjusted salaries. Would you
                        like to save changes? You may click &quot;Cancel&quot;
                        then click the &quot;Share&quot; button to save your
                        changes.
                      </p>
                    </div>

                    <div className="mt-4">
                      <LegacyButton
                        variant={LegacyButtonVariant.PROMPT_CONFIRM_DESTRUCTIVE}
                        onClick={discardChanges}
                        className="mt-4"
                      >
                        No, discard
                      </LegacyButton>

                      <LegacyButton
                        variant={LegacyButtonVariant.PROMPT_CANCEL_DESTRUCTIVE}
                        onClick={resetBlocker}
                        className="mt-4 ml-4"
                      >
                        Cancel
                      </LegacyButton>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  );
};

// check if two arrays have the same items, regardless of order
const areStringArraysEqual = <T extends string>(a: T[], b: T[]) => {
  return a.length === b.length && a.every((item) => b.includes(item));
};
